import React from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { cn } from '@core/utils/cn';
import { GoalDto } from '@api/FitnessActivityClient';
import {
    describeGoal,
    describeReward,
    goalActivityScope,
    goalTitle,
    isGoalEnded,
    ruleTypeLabels
} from '@core/goals/goal-summary';

interface GoalCardProps {
    goal: GoalDto;
    onEdit: (goal: GoalDto) => void;
    onDelete: (goal: GoalDto) => void;
}

const formatDateWindow = (goal: GoalDto): string | undefined => {
    if (!goal.dayFrom && !goal.dayTo) {
        return undefined;
    }
    if (goal.dayFrom && goal.dayTo) {
        return `${goal.dayFrom} → ${goal.dayTo}`;
    }
    return goal.dayFrom ? `From ${goal.dayFrom}` : `Until ${goal.dayTo}`;
};

const IconAction = ({
    title,
    onClick,
    className,
    children
}: {
    title: string;
    onClick(): void;
    className?: string;
    children: React.ReactNode;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={title}
                className={className}
                onClick={onClick}
            >
                {children}
            </Button>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
    </Tooltip>
);

export const GoalCard: React.FC<GoalCardProps> = ({
    goal,
    onEdit,
    onDelete
}) => {
    const summary = describeGoal(goal.rule, goal.recurrence);
    const title = goalTitle(goal);
    const ended = isGoalEnded(goal);
    const scope = goalActivityScope(goal.rule);
    const dateWindow = formatDateWindow(goal);
    const levelCount = goal.rule.levels?.length ?? 0;
    const rewards = goal.rewardConfigs
        .map((rewardConfig) => describeReward(rewardConfig))
        .filter((reward): reward is string => !!reward);

    const unnamedSubtitle =
        scope && !summary.includes(scope)
            ? scope
            : ruleTypeLabels[goal.rule.type];
    const subtitle = title === summary ? unnamedSubtitle : summary;

    const copyKey = () => navigator.clipboard.writeText(goal.key);

    return (
        <Card
            className={cn(
                'h-full py-4 shadow-none',
                !goal.visibleInApp && 'opacity-60'
            )}
        >
            <CardContent className="flex h-full flex-col gap-3 px-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">
                            {title}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            {subtitle}
                        </p>
                    </div>
                    <div className="-mr-2 -mt-1 flex shrink-0">
                        <IconAction
                            title={`Copy goal key ${goal.key}`}
                            onClick={copyKey}
                        >
                            <Copy />
                        </IconAction>
                        <IconAction
                            title="Edit goal"
                            onClick={() => onEdit(goal)}
                        >
                            <Pencil />
                        </IconAction>
                        <IconAction
                            title="Delete goal"
                            className="text-destructive"
                            onClick={() => onDelete(goal)}
                        >
                            <Trash2 />
                        </IconAction>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1">
                    {ended && <Badge variant="muted">Ended</Badge>}
                    {!goal.visibleInApp && (
                        <Badge variant="warning">Hidden</Badge>
                    )}
                    {goal.streakEnabled && (
                        <Badge variant="outline">Streak</Badge>
                    )}
                    {levelCount > 0 && (
                        <Badge variant="outline">{levelCount} levels</Badge>
                    )}
                    {dateWindow && (
                        <Badge variant="outline">{dateWindow}</Badge>
                    )}
                </div>

                <div className="grow">
                    {rewards.length ? (
                        rewards.map((reward, index) => (
                            <p
                                key={index}
                                className="text-sm text-muted-foreground"
                            >
                                {reward}
                            </p>
                        ))
                    ) : (
                        <p className="text-sm text-warning">
                            No reward configured
                        </p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
